import { Controller } from '@hotwired/stimulus';
// Import EXPLICITE plutôt que `window.Turbo` (idiome de `revision_preview_controller.js`) : un global absent
// rendrait le vidage du cache inerte sans un mot.
import * as Turbo from '@hotwired/turbo';
import { trapFocus } from '../utils/focus_trap.js';
import { inDropdownPortal, otherDialogOpen, tabOutOfDropdownPortal } from './sidebar_controller.js';

/**
 * Sidebar Collapse Controller — repli du menu latéral de l'agence (refonte de l'espace
 * chantier V2, G-2, G-10 ; décisions 02, 15, P9).
 *
 * Monté SUR l'`<aside id="app-sidebar">` de `Layouts::SidebarComponent` (`collapsible: true`).
 * Un seul DOM pour toutes les formes : le CSS lit `data-sidebar-collapse-collapsed-value` et
 * `aria-modal` sur l'`<aside>` (`Layouts::Sidebar::RailVariants`) ; ce contrôleur écrit ces
 * attributs et ceux que le CSS ne peut pas porter.
 *
 * Trois paliers (`matchMedia`, mêmes seuils que `md:` et `xl:`) :
 * - ≥ 1280 px (`desktop`) : rail ou déplié selon la préférence. Le bouton et `[` basculent
 *   la valeur et l'ÉCRIVENT (PATCH d'une valeur explicite `collapsed=true|false`, jamais une
 *   bascule côté serveur). Échec réseau : l'état local reste, aucune alerte. Le dernier choix
 *   est retenu au niveau du module et réappliqué au `connect` et après un morph quand la valeur
 *   rendue, périmée, en diffère (`reconcileCollapsed`) ; oublié dès qu'elle concorde, et au
 *   rendu d'une page sans le menu (`turbo:load` : déconnexion, portails). La bascule jette aussi
 *   les copies de pages gardées par Turbo (`Turbo.cache`) : elles portent le menu d'AVANT, et le
 *   ressortaient au retour arrière (restauration, aucune requête) ou en aperçu d'un clic, une fois
 *   le choix oublié (menu réduit qui se rouvrait, 17/10).
 * - 768-1279 px (`tablet`) : rail IMPOSÉ, quelle que soit la préférence ; le bouton et `[`
 *   ouvrent une superposition (`role="dialog"`, `aria-modal="true"`), rien n'est écrit.
 *   « Fermer le menu » en tête (`close`), Échap, un clic hors du menu ou une visite Turbo la
 *   ferment ; Tab y reste piégé, y compris depuis le menu du profil porté dedans.
 * - < 768 px (`phone`) : pas de rail. La feuille « Menu » est le tiroir de
 *   `sidebar_controller.js` (événements `sidebar:opened` / `sidebar:closed`) : ce contrôleur
 *   y pose `role="dialog"`, et `inert` sur le menu fermé.
 *
 * Attributs réécrits à chaque rendu (au `connect`, à chaque changement de palier, de valeur,
 * d'ouverture, et sur `turbo:morph-element` : idiomorph garde l'`<aside>` et lui remet les
 * attributs du serveur sans rejouer `connect()`, précédent `visa_tracker_controller.js`) :
 * - `<aside>` : `role`, `aria-modal`, `aria-label` (seulement avec `dialogLabel`), `inert`,
 *   `data-tooltips="off"` hors forme rail (lu par `tooltip_controller.js`), `data-sidebar-rail`
 *   sur la forme rail (lu par `dropdown_controller.js`, `side: auto`) ;
 * - bouton de repli : `aria-expanded`, `aria-controls`, `aria-keyshortcuts`, et `aria-label`
 *   + `title` quand les libellés arrivent du serveur (`expandLabel` / `collapseLabel` ; sans
 *   eux, les libellés rendus par le serveur restent tels quels).
 *
 * Raccourci `[` (décision P9, écart WCAG 2.1.4 consigné dans l'ADR-020) : `event.key`, jamais
 * `event.code` ; ignoré dans un champ, pendant une composition, avec Cmd, avec Ctrl sans Alt
 * (AZERTY : AltGr + 5 = Ctrl + Alt), en répétition, et quand un autre dialogue est ouvert.
 * Planning en plein écran (`planning-fullscreen-open` sur `<body>`, `planning_fullscreen_controller.js`) :
 * aucune ouverture ni bascule du menu, `[` reste à la page (QA de la PR 1, F5).
 *
 * Au `connect` (≥ 768 px), l'entrée active (`aria-current`) défile dans la colonne défilante quand celle-ci la
 * cache (`revealScrollTop`, au plus près) ; la page, elle, ne défile jamais.
 *
 * Targets:
 * - toggle: le bouton « Réduire le menu » / « Déplier le menu »
 * - scroll (facultative): la colonne défilante des entrées
 *
 * Values:
 * - collapsed (Boolean): préférence rendue par le serveur (`users.sidebar_collapsed`)
 * - persistUrl (String): `PATCH /mon-profil/menu` ; vide = rien n'est écrit
 * - expandLabel / collapseLabel (String): « Déplier le menu » / « Réduire le menu »
 * - dialogLabel (String): nom accessible de la superposition et de la feuille ouvertes
 *
 * Actions:
 * - click->sidebar-collapse#toggle (bouton de repli)
 * - click->sidebar-collapse#close (« Fermer le menu » en tête, sans effet hors superposition)
 *
 * @example
 * <aside id="app-sidebar" aria-label="Navigation"
 *        data-controller="sidebar-collapse"
 *        data-sidebar-collapse-collapsed-value="false"
 *        data-sidebar-collapse-persist-url-value="/mon-profil/menu"
 *        data-sidebar-collapse-expand-label-value="Déplier le menu"
 *        data-sidebar-collapse-collapse-label-value="Réduire le menu"
 *        data-sidebar-collapse-dialog-label-value="Menu">
 *   …
 *   <button type="button" data-sidebar-collapse-target="toggle"
 *           data-action="click->sidebar-collapse#toggle"
 *           aria-expanded="true" aria-controls="app-sidebar" aria-keyshortcuts="[">…</button>
 * </aside>
 */

export const MEDIA_MD = '(min-width: 48rem)';
export const MEDIA_XL = '(min-width: 80rem)';
export const SHORTCUT_KEY = '[';
// Lu par `dropdown_controller.js` (`side: auto`) : présent sur l'`<aside>` en forme rail.
export const RAIL_ATTRIBUTE = 'data-sidebar-rail';

const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

// Posée sur `<body>` par `planning_fullscreen_controller.js` (`#enter` / `#exit`) tant que le planning
// est en plein écran.
export const PLANNING_FULLSCREEN_BODY_CLASS = 'planning-fullscreen-open';

/**
 * Le planning est-il en plein écran (pur) ? Le menu ne s'ouvre ni ne bascule par-dessus.
 * @param {Document} [root]
 * @returns {boolean}
 */
export function planningFullscreenOpen(root = document) {
  return Boolean(root?.body?.classList?.contains(PLANNING_FULLSCREEN_BODY_CLASS));
}

/**
 * Palier de largeur (pur).
 * @param {{md: boolean, xl: boolean}} matches - `(min-width: 48rem)`, `(min-width: 80rem)`
 * @returns {'phone'|'tablet'|'desktop'}
 */
export function sidebarTier({ md, xl }) {
  if (xl) return 'desktop';
  if (md) return 'tablet';
  return 'phone';
}

/**
 * Forme du menu (pur) : rail, dialogue ouvert (superposition ou feuille), `inert`.
 * Entre 768 et 1279 px le rail est IMPOSÉ : la préférence n'y joue aucun rôle.
 * @param {{tier: string, collapsed: boolean, overlayOpen: boolean, drawerOpen: boolean}} state
 * @returns {{rail: boolean, dialog: boolean, inert: boolean}}
 */
export function sidebarState({ tier, collapsed, overlayOpen, drawerOpen }) {
  if (tier === 'desktop') return { rail: Boolean(collapsed), dialog: false, inert: false };
  if (tier === 'tablet') return { rail: !overlayOpen, dialog: Boolean(overlayOpen), inert: false };
  return { rail: false, dialog: Boolean(drawerOpen), inert: !drawerOpen };
}

/**
 * Attributs du bouton de repli (pur). Sur le rail il DÉPLIE (`aria-expanded="false"`), sur le
 * menu déplié il RÉDUIT. Sans libellé fourni, `aria-label` et `title` valent `null` : le rendu
 * du serveur reste.
 * @param {{rail: boolean, expandLabel?: string, collapseLabel?: string}} options
 * @returns {{ariaExpanded: string, ariaLabel: string|null, title: string|null}}
 */
export function toggleAttributes({ rail, expandLabel = '', collapseLabel = '' }) {
  const label = rail ? expandLabel : collapseLabel;
  return {
    ariaExpanded: String(!rail),
    ariaLabel: label || null,
    // Même forme que `Layouts::SidebarComponent#toggle_title` : « Libellé ( [ ) ».
    title: label ? `${label} ( ${SHORTCUT_KEY} )` : null,
  };
}

/**
 * La frappe est-elle le raccourci `[` du menu (pur) ?
 * @param {KeyboardEvent} event
 * @returns {boolean}
 */
export function isShortcutEvent(event) {
  if (event.key !== SHORTCUT_KEY) return false;
  if (event.defaultPrevented || event.isComposing || event.repeat) return false;
  if (event.metaKey) return false;
  if (event.ctrlKey && !event.altKey) return false;

  const target = event.target;
  if (target?.isContentEditable) return false;
  if (typeof target?.closest === 'function' && target.closest(EDITABLE_SELECTOR)) return false;
  return true;
}

/**
 * Options du `fetch` de la préférence (pur). Rejouées À L'IDENTIQUE par
 * `test/integration/profiles/sidebar_preferences_test.rb#patch_preference`.
 * @param {boolean} collapsed
 * @param {string} csrfToken - contenu du `meta[name="csrf-token"]`, `''` s'il manque
 * @returns {RequestInit}
 */
export function preferenceRequest(collapsed, csrfToken) {
  return {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      Accept: '*/*',
      'X-CSRF-Token': csrfToken,
    },
    body: `collapsed=${collapsed ? 'true' : 'false'}`,
    credentials: 'same-origin',
    // La bascule précède souvent un clic de navigation : la requête survit au déchargement.
    keepalive: true,
  };
}

/**
 * Valeur à appliquer, entre la valeur RENDUE par le serveur et le dernier choix explicite retenu
 * (pur). Un choix qui diffère l'emporte et reste retenu ; une valeur rendue qui concorde montre que
 * le serveur a rattrapé : le choix est oublié, et la valeur rendue refait foi ensuite.
 * @param {boolean} rendered - `collapsed` rendu par le serveur (peut être périmé)
 * @param {boolean|null} remembered - dernier choix explicite, `null` = aucun
 * @returns {{collapsed: boolean, remembered: boolean|null}}
 */
export function reconcileCollapsed(rendered, remembered) {
  if (remembered === null || remembered === rendered)
    return { collapsed: rendered, remembered: null };
  return { collapsed: remembered, remembered };
}

/**
 * `scrollTop` de la colonne défilante qui montre l'entrée active « au plus près » (pur, même règle que
 * `scrollIntoView({ block: 'nearest' })`, sans en faire défiler les ancêtres) : rien si elle est visible ;
 * dessous, son bas sur le bas de la colonne ; dessus, ou plus haute que la colonne, son haut sur le haut.
 * Positions à l'écran (`getBoundingClientRect`).
 * @param {{scrollTop: number, areaTop: number, areaHeight: number, itemTop: number, itemHeight: number}} geometry
 * @returns {number}
 */
export function revealScrollTop({ scrollTop, areaTop, areaHeight, itemTop, itemHeight }) {
  const offset = itemTop - areaTop;
  if (offset < 0) return scrollTop + offset;
  const overflow = offset + itemHeight - areaHeight;
  if (overflow > 0) return scrollTop + Math.min(overflow, offset);
  return scrollTop;
}

// Écritures en file, dans l'ordre des gestes : la dernière valeur envoyée est la dernière
// écrite, même si deux bascules rapides se croisent sur le réseau.
let persistChain = Promise.resolve();

// Dernier choix explicite au bureau (bouton ou `[`), `null` = aucun. Au niveau du MODULE : il
// survit aux visites Drive, pas à un rechargement complet (qui relit un serveur ayant reçu le
// PATCH). La valeur rendue peut être PÉRIMÉE : page préchargée au survol avant le PATCH,
// instantané du cache pour Retour, GET servi avant que le PATCH ne soit commité. Rattrapage d'une
// session de page, jamais une source persistante : la préférence vit sur le serveur (décision 02).
let rememberedCollapsed = null;

/** Oublie le dernier choix (ce que fait un rechargement complet ; utilisé par les tests). */
export function forgetCollapsedChoice() {
  rememberedCollapsed = null;
}

const MENU_SELECTOR = '[data-controller~="sidebar-collapse"]';

// Une page rendue SANS le menu (connexion Devise, portails…) efface le choix. La mémoire ne connaît
// pas l'utilisateur : A se déconnecte et B se connecte sans rechargement complet (DELETE de session
// par Turbo Drive, même JS sur la page de connexion), B hériterait sinon du choix de A. La
// déconnexion passe toujours par une page sans menu. Écouteur de `turbo:load` au niveau du module :
// une seule référence, que le DOM n'inscrit qu'une fois.
function forgetChoiceOffMenu() {
  if (!document.querySelector(MENU_SELECTOR)) forgetCollapsedChoice();
}

function setAttribute(element, name, value) {
  if (value === null || value === undefined) element.removeAttribute(name);
  else element.setAttribute(name, value);
}

export default class extends Controller {
  static targets = ['toggle', 'scroll'];

  static values = {
    collapsed: { type: Boolean, default: false },
    persistUrl: { type: String, default: '' },
    expandLabel: { type: String, default: '' },
    collapseLabel: { type: String, default: '' },
    dialogLabel: { type: String, default: '' },
  };

  connect() {
    this.overlayOpen = false;
    this.overlayOpener = null;
    this.drawerOpen = false;
    this.serverLabel = this.element.getAttribute('aria-label');
    // Avant `ready` : `collapsedValueChanged` ne rend rien, `render()` ci-dessous s'en charge.
    this.applyRememberedChoice();

    this.mdQuery = window.matchMedia(MEDIA_MD);
    this.xlQuery = window.matchMedia(MEDIA_XL);
    this.onMediaChange = this.handleMediaChange.bind(this);
    this.mdQuery.addEventListener('change', this.onMediaChange);
    this.xlQuery.addEventListener('change', this.onMediaChange);

    this.onKeydown = this.handleKeydown.bind(this);
    this.onBeforeVisit = this.handleBeforeVisit.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('turbo:before-visit', this.onBeforeVisit);
    document.addEventListener('click', this.onDocumentClick);

    this.onMorph = this.handleMorph.bind(this);
    this.onDrawerOpened = this.handleDrawerOpened.bind(this);
    this.onDrawerClosed = this.handleDrawerClosed.bind(this);
    this.element.addEventListener('turbo:morph-element', this.onMorph);
    this.element.addEventListener('sidebar:opened', this.onDrawerOpened);
    this.element.addEventListener('sidebar:closed', this.onDrawerClosed);

    this.ready = true;
    this.render();
    this.revealActiveEntry();
  }

  disconnect() {
    this.ready = false;
    this.mdQuery?.removeEventListener('change', this.onMediaChange);
    this.xlQuery?.removeEventListener('change', this.onMediaChange);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('turbo:before-visit', this.onBeforeVisit);
    document.removeEventListener('click', this.onDocumentClick);
    this.element.removeEventListener('turbo:morph-element', this.onMorph);
    this.element.removeEventListener('sidebar:opened', this.onDrawerOpened);
    this.element.removeEventListener('sidebar:closed', this.onDrawerClosed);
    // Le rendu de la page SUIVANTE dira si elle porte le menu. Écouteur de MODULE, ponctuel
    // (`once`) et unique (même fonction : le DOM ne l'inscrit qu'une fois) : il ne s'accumule pas.
    document.addEventListener('turbo:load', forgetChoiceOffMenu, { once: true });
  }

  // Stimulus l'appelle aussi AVANT `connect()` avec la valeur initiale : `ready` l'ignore.
  collapsedValueChanged() {
    if (this.ready) this.render();
  }

  /** Bouton de repli. Sur la superposition, le focus reste sur le bouton cliqué. */
  toggle() {
    this.flip({ restoreFocus: false });
  }

  /**
   * « Fermer le menu » en tête de la superposition : la ferme et rend le focus au déclencheur.
   * Le même bouton ferme la feuille du téléphone par `sidebar#close` ; ici, hors superposition, rien.
   */
  close() {
    this.setOverlay(false, { restoreFocus: true });
  }

  get tier() {
    return sidebarTier({ md: this.mdQuery.matches, xl: this.xlQuery.matches });
  }

  // ── Gestes ──

  flip({ restoreFocus }) {
    if (planningFullscreenOpen()) return;
    const tier = this.tier;
    if (tier === 'desktop') {
      const next = !this.collapsedValue;
      rememberedCollapsed = next;
      this.collapsedValue = next;
      this.render();
      this.persist(next);
      Turbo.cache.clear();
    } else if (tier === 'tablet') {
      this.setOverlay(!this.overlayOpen, { restoreFocus });
    }
    // < 768 px : pas de rail, la feuille « Menu » s'ouvre par la barre du téléphone.
  }

  setOverlay(open, { restoreFocus = false } = {}) {
    if (open === this.overlayOpen) return;

    if (open) {
      const active = document.activeElement;
      this.overlayOpener = active && active !== document.body ? active : null;
    }
    this.overlayOpen = open;
    this.render();

    if (open) {
      // Dialogue modal : le focus y entre (ouverture par `[` depuis la page).
      if (!this.element.contains(document.activeElement) && this.hasToggleTarget) {
        this.toggleTarget.focus();
      }
      return;
    }

    const opener = this.overlayOpener;
    this.overlayOpener = null;
    if (!restoreFocus) return;
    const active = document.activeElement;
    if (active && active !== document.body && !this.element.contains(active)) return;
    if (opener?.isConnected && !this.element.contains(opener)) opener.focus();
    else if (this.hasToggleTarget) this.toggleTarget.focus();
  }

  /**
   * Réapplique le dernier choix quand la valeur rendue (au `connect`, après un morph) en diffère ;
   * l'oublie quand elle concorde. N'écrit RIEN au serveur : le PATCH du geste est déjà en file.
   */
  applyRememberedChoice() {
    const { collapsed, remembered } = reconcileCollapsed(this.collapsedValue, rememberedCollapsed);
    rememberedCollapsed = remembered;
    if (collapsed !== this.collapsedValue) this.collapsedValue = collapsed;
  }

  /**
   * Au chargement, l'entrée active (`aria-current`) défile dans la colonne quand celle-ci la cache (QA de la
   * PR 1 : 140 px visibles à 844 × 390). Seule la colonne bouge, jamais la page ; rien au téléphone (la feuille
   * « Menu » fermée reste telle quelle). Synchrone, aucun écouteur : rien à retirer au `disconnect`.
   */
  revealActiveEntry() {
    if (!this.hasScrollTarget || this.tier === 'phone') return;
    const area = this.scrollTarget;
    const item = area.querySelector('[aria-current]');
    if (!item) return;

    const areaRect = area.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const next = revealScrollTop({
      scrollTop: area.scrollTop,
      areaTop: areaRect.top,
      areaHeight: areaRect.height,
      itemTop: itemRect.top,
      itemHeight: itemRect.height,
    });
    if (next !== area.scrollTop) area.scrollTop = next;
  }

  persist(collapsed) {
    const url = this.persistUrlValue;
    if (!url) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
    persistChain = persistChain
      .then(() => fetch(url, preferenceRequest(collapsed, csrfToken)))
      .catch(() => undefined);
  }

  // ── Écouteurs ──

  handleKeydown(event) {
    if (event.key === 'Escape') {
      if (!this.overlayOpen || event.defaultPrevented) return;
      if (otherDialogOpen(this.element)) return;
      event.preventDefault();
      this.setOverlay(false, { restoreFocus: true });
      return;
    }

    if (event.key === 'Tab') {
      if (!this.overlayOpen || otherDialogOpen(this.element)) return;
      // Le menu du profil est porté DANS l'aside quand il est en dialogue (portalHost de
      // dropdown_controller.js) : une tabulation qui en part reste dans l'aside.
      if (tabOutOfDropdownPortal(this.element, event)) return;
      trapFocus(this.element, event);
      return;
    }

    if (!isShortcutEvent(event)) return;
    if (this.tier === 'phone' || otherDialogOpen(this.element) || planningFullscreenOpen()) return;
    event.preventDefault();
    this.flip({ restoreFocus: true });
  }

  handleDocumentClick(event) {
    if (!this.overlayOpen) return;
    const target = event.target;
    if (this.element.contains(target)) return;
    if (inDropdownPortal(target)) return;
    this.setOverlay(false);
  }

  handleBeforeVisit() {
    this.setOverlay(false);
  }

  handleMediaChange() {
    // Changer de palier ferme la superposition : elle n'existe qu'entre 768 et 1279 px.
    this.overlayOpen = false;
    this.overlayOpener = null;
    this.render();
  }

  handleDrawerOpened(event) {
    if (event.target !== this.element) return;
    this.drawerOpen = true;
    this.render();
  }

  handleDrawerClosed(event) {
    if (event.target !== this.element) return;
    this.drawerOpen = false;
    this.render();
  }

  // Idiomorph a remis les attributs du serveur sur l'`<aside>` et ses enfants (l'événement de
  // l'hôte part APRÈS ceux des descendants, qui remontent jusqu'ici : seul l'hôte compte).
  handleMorph(event) {
    if (event.target !== this.element) return;

    this.serverLabel = this.element.getAttribute('aria-label');
    this.applyRememberedChoice();
    this.render();
  }

  // ── Rendu ──

  render() {
    if (!this.ready) return;

    const state = sidebarState({
      tier: this.tier,
      collapsed: this.collapsedValue,
      overlayOpen: this.overlayOpen,
      drawerOpen: this.drawerOpen,
    });
    const aside = this.element;

    setAttribute(aside, RAIL_ATTRIBUTE, state.rail ? 'true' : null);
    setAttribute(aside, 'data-tooltips', state.rail ? null : 'off');
    setAttribute(aside, 'role', state.dialog ? 'dialog' : null);
    setAttribute(aside, 'aria-modal', state.dialog ? 'true' : null);
    setAttribute(
      aside,
      'aria-label',
      state.dialog && this.dialogLabelValue ? this.dialogLabelValue : this.serverLabel
    );
    setAttribute(aside, 'inert', state.inert ? '' : null);

    if (!this.hasToggleTarget) return;
    const toggle = this.toggleTarget;
    const attributes = toggleAttributes({
      rail: state.rail,
      expandLabel: this.expandLabelValue,
      collapseLabel: this.collapseLabelValue,
    });
    toggle.setAttribute('aria-expanded', attributes.ariaExpanded);
    if (attributes.ariaLabel) toggle.setAttribute('aria-label', attributes.ariaLabel);
    if (attributes.title) toggle.setAttribute('title', attributes.title);
    if (aside.id) toggle.setAttribute('aria-controls', aside.id);
    toggle.setAttribute('aria-keyshortcuts', SHORTCUT_KEY);
  }
}
